import React, { useEffect, useState, useRef, type RefObject } from "react";
import { Button } from "@tennr/frontend/components/Button";
import { AnimatedLogo } from "@tennr/frontend/components/AnimatedLogo";
import { motion, useScroll, useTransform } from "framer-motion";
import { Heading } from "@tennr/frontend/components/Heading";
import { DynamicText } from "@tennr/frontend/components/DynamicText";
import { Link } from "@tanstack/react-router";
import { useIsDesktop } from "@tennr/frontend/hooks/useMediaQuery";

import "./SiteFooter.css";

export interface SiteFooterLink {
  label: string;
  href: string;
  isNew?: boolean;
}

export interface SiteFooterProps {
  // tagline section
  taglineTitle?: string;
  taglineHeading?: string;
  taglineBody?: string;
  taglineCtaText?: string;
  taglineCtaHref?: string;
  taglinePosition?: "left" | "center" | "right";
  taglineTextAlign?: "left" | "center" | "right";
  taglineMobilePosition?: "left" | "center" | "right";

  // navigation links
  primaryLinks?: SiteFooterLink[];
  primaryLinks2?: SiteFooterLink[];
  secondaryLinks?: SiteFooterLink[];

  // copyright text
  copyrightText?: string;

  // background image for the footer
  bgImage?: string;
  bgImageVert?: string;

  className?: string;
  buttonWidth?: string | number;
}

export const SiteFooter: React.FC<SiteFooterProps> = ({
  taglineTitle = "Let's Work Together",
  taglineHeading,
  taglineBody,
  taglineCtaText = "Get Started",
  taglineCtaHref = "/book-a-demo",
  taglinePosition = "right",
  taglineTextAlign = "left",
  taglineMobilePosition = "center",
  primaryLinks = [
    { label: "Product", href: "/product" },
    { label: "About Us", href: "/about" },
    { label: "Press", href: "/press" },
    { label: "Careers", href: "/careers", isNew: true },
    { label: "Customer Stories", href: "/customer-stories" },
    { label: "Privacy Policy", href: "/privacy-policy" },
    { label: "Fax It", href: "/fax-it" },
  ],
  copyrightText = "",
  className = "",
  buttonWidth = "168px",
  bgImage,
  bgImageVert,
}) => {
  const sectionRef = useRef<HTMLDivElement>(null);
  const isDesktop = useIsDesktop();

  // track scroll progress for this section
  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start end", "end end"],
  });

  // transform scroll progress to opacity values for background
  const footerBgOpacity = useTransform(scrollYProgress, [0, 0.2, 0.5, 0.7, 1], [0, 0, 0, 0.5, 1]);

  // get background image based on orientation
  const [currentBgImage, setCurrentBgImage] = useState<string | undefined>(bgImage);

  useEffect(() => {
    const handleOrientationChange = () => {
      const isPortrait = window.matchMedia("(orientation: portrait)").matches;
      setCurrentBgImage(isPortrait ? bgImageVert : bgImage);
    };

    handleOrientationChange(); // Initial check
    window.addEventListener("resize", handleOrientationChange);

    return () => {
      window.removeEventListener("resize", handleOrientationChange);
    };
  }, [bgImage, bgImageVert]);

  const defaultTaglineHeading = isDesktop ? (
    "No backlog, no bottlenecks—just growth"
  ) : (
    <>
      No backlog,
      <br /> no bottlenecks—
      <br />
      just growth.
    </>
  );
  return (
    <footer className={`site-footer ${className}`} ref={sectionRef}>
      {/* Fixed image Background */}
      <motion.div
        className="site-footer-background"
        style={{
          backgroundImage: currentBgImage ? `url(${currentBgImage})` : undefined,
          opacity: footerBgOpacity,
        }}
      ></motion.div>

      {/* Tagline Section */}
      {(taglineTitle || taglineHeading || taglineBody) && (
        <div className={`site-footer-tagline site-footer-tagline--${taglinePosition}`}>
          <div className="site-footer-tagline-inner">
            <div className="site-footer-tagline-content">
              <div className="site-footer-tagline-group">
                {(taglineTitle || taglineHeading || taglineBody) && (
                  <Heading
                    title={taglineTitle}
                    heading={taglineHeading || defaultTaglineHeading}
                    body={taglineBody}
                    position="left"
                    textAlign={taglineTextAlign}
                    mobilePosition="center"
                    mobileTextAlign={taglineMobilePosition ? "center" : undefined}
                    variant="hero"
                  />
                )}
                {taglineCtaText && (
                  <Link to={taglineCtaHref} style={{ textDecoration: "none" }}>
                    <Button
                      text={taglineCtaText}
                      className="site-footer-tagline-cta"
                      width={buttonWidth}
                      minWidth="168px"
                      minHeight="58px"
                    />
                  </Link>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Main Footer */}
      <div className="site-footer-main">
        {/* Left Column - Logo */}
        <a href="/" className="site-footer-logo-link">
          <motion.div className="site-footer-logo-wrapper">
            <AnimatedLogo width="100%" fillColor="#EEDFD1" opacity={1} enableScrollEffects={false} />
          </motion.div>
        </a>

        {/* Right Column - Links */}
        <nav className={`site-footer-nav ${primaryLinks.length < 7 ? "alt-cols" : ""}`}>
          {primaryLinks.map((link, index) => (
            <DynamicText key={index} as="a" href={link.href} className="site-footer-link">
              {link.label}
            </DynamicText>
          ))}
          {/* {primaryLinks.map((link, index) => (
            <DynamicText
              key={index}
              as="a"
              href={link.href}
              className="site-footer-link"
              target="_blank"
              rel="noreferrer noopener"
            >
              {link.label}
            </DynamicText>
          ))} */}
        </nav>

        {/* Copyright */}
        <div className="site-footer-bottom">
          <img
            src="https://c.animaapp.com/1tp8daPr/img/image-1256@2x.png"
            alt="HIPAA Compliant"
            className="remote-image"
            style={{ height: "46px", width: "auto" }}
            draggable={false}
          />
          <img
            src="https://c.animaapp.com/1tp8daPr/img/image-1257@2x.png"
            alt="SOC II Compliant"
            className="remote-image"
            style={{ height: "46px", width: "auto" }}
            draggable={false}
          />
      </div>
      <div className="site-footer-address">
          <p>275 7th Ave, Floor 21
              <br/>New York, NY 10001</p>
      </div>
        <div className="site-footer-bottom">
          <a href="https://www.youtube.com/@Tennr-Official" target="_blank" rel="noreferrer noopener">
            <img
              src="/svg/youtube.svg"
              alt="Youtube"
              className="social-logo"
              style={{ height: "19px", width: "auto" }}
              draggable={false}
            />
          </a>
          <a href="https://linkedIn.com/company/tennrai/" target="_blank" rel="noreferrer noopener">
            <img
              src="/svg/linkedIn.svg"
              alt="Linked In"
              className="social-logo"
              style={{ height: "27px", width: "auto" }}
              draggable={false}
            />
          </a>
        </div>
        {copyrightText && <p className="site-footer-copyright">{copyrightText}</p>}
      </div>
    </footer>
  );
};
